import { TokenUserRoles } from 'api/users/schemas/Users';

export const systemRoles = [
  TokenUserRoles.COMPANY_ADMIN,
  TokenUserRoles.USER,
  TokenUserRoles.VIEWER,
  TokenUserRoles.WELCOME_USER,
  //
  TokenUserRoles.SUPER_ADMIN,
];

export const mappedSystemRoles = {
  [TokenUserRoles.COMPANY_ADMIN]: 'Company Admin',
  [TokenUserRoles.USER]: 'User',
  [TokenUserRoles.VIEWER]: 'Viewer',
  [TokenUserRoles.WELCOME_USER]: 'Welcome User',
  //
  [TokenUserRoles.SUPER_ADMIN]: 'Super Admin',
  [TokenUserRoles.PROSPECT]: 'Prospect',
};

export const getSystemRoleFromName = (name: string) => {
  return (
    Object.keys(mappedSystemRoles).find(
      (key) => mappedSystemRoles[key as TokenUserRoles] === name,
    ) ?? name
  );
};

export const getTextForSystemRole = (role: string) =>
  mappedSystemRoles[role as TokenUserRoles] || role;

export const isSystemRole = (role: string) =>
  systemRoles.includes(role as TokenUserRoles);

export const getColorForCustomRole = (color: string) => {
  switch (color) {
    case '#7E77B3':
      return 'text-content-base-default';
    case '#A7FFB2':
      return 'text-content-flip-default';
    case '#1B4A50':
      return 'text-content-base-default';
    case '#A7F4FF':
      return 'text-content-flip-default';
    case '#7B7BE9':
      return 'text-content-base-default';
    default:
      return 'text-content-base-default';
  }
};

export const getColorsForRole = (
  role: string,
  color: string | null | undefined,
) => {
  if (color) {
    return {
      backgroundColor: color,
      color: getColorForCustomRole(color),
    };
  }
  switch (role) {
    case TokenUserRoles.COMPANY_ADMIN:
      return {
        backgroundColor: '#1A4BAB',
        color: 'text-content-base-default',
      };
    case TokenUserRoles.USER:
      return {
        backgroundColor: '#96baff',
        color: 'text-content-flip-default',
      };
    case TokenUserRoles.VIEWER:
      return {
        backgroundColor: '#96baff',
        color: 'text-content-flip-default',
      };
    case TokenUserRoles.WELCOME_USER:
      return {
        backgroundColor: '#FBB979',
        color: 'text-content-flip-default',
      };
    default:
      return {
        backgroundColor: '#96baff',
        color: 'text-content-flip-default',
      };
  }
};

export const permissionsMapping = {
  'hidden:accessexpress': [],
  'read:accessexpress': ['read:accessexpress'],
  'readWrite:accessexpress': ['read:accessexpress', 'write:accessexpress'],
  'hidden:nocexpress': [],
  'read:nocexpress': ['read:nocexpress'],
  'readWrite:nocexpress': ['read:nocexpress', 'write:nocexpress'],
  'hidden:techexpress': [],
  'read:techexpress': ['read:techexpress'],
  'readWrite:techexpress': ['read:techexpress', 'write:techexpress'],
};

export const getCustomRolePermissions = (permissions: string[]) => {
  return permissions
    .map(
      (permission) =>
        permissionsMapping[permission as keyof typeof permissionsMapping],
    )
    .flat();
};

const SERVICES = ['accessexpress', 'techexpress', 'nocexpress'] as const;
type Service = (typeof SERVICES)[number];

export const transformPermissions = (permissions: string[]): string[] => {
  // Parse permissions into action-service pairs
  const permissionMap = new Map<Service, Set<string>>(
    SERVICES.map((service) => [service, new Set()]),
  );

  permissions.forEach((perm) => {
    const [action, service] = perm.split(':');
    if (
      SERVICES.includes(service as Service) &&
      ['read', 'write', 'manage'].includes(action)
    ) {
      permissionMap.get(service as Service)!.add(action);
    }
  });

  // Define permission output based on action combinations
  const getPermissionOutput = (
    actions: Set<string>,
    service: Service,
  ): string => {
    if (actions.size === 0) return `hidden:${service}`;
    if (actions.has('write') || actions.has('manage'))
      return `readWrite:${service}`;
    return `read:${service}`; // Only read action remains
  };

  return SERVICES.map((service) =>
    getPermissionOutput(permissionMap.get(service)!, service),
  );
};

export const getPermissionDisplayObject = (
  permissions: string[],
): Record<string, string> => {
  const displayMap: Record<string, string> = {
    accessexpress: 'Hidden',
    techexpress: 'Hidden',
    nocexpress: 'Hidden',
  };

  permissions.forEach((permission) => {
    const [level, service] = permission.split(':');

    switch (level) {
      case 'hidden':
        displayMap[service] = 'Hidden';
        break;
      case 'read':
        displayMap[service] = 'Read only';
        break;
      case 'readWrite':
        displayMap[service] = 'Read + write';
        break;
    }
  });

  return displayMap;
};
